// ============================================================================
// USE ENTITY TREE HOOK
// Provides entity tree data with phase-based filtering and validation mutations
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSessionStore } from '@/stores/sessionStore'
import { useJourneyStore } from '@/stores/journeyStore'
import {
  entityTreeApi,
  type EntityTreeResponse,
  type EntityTreeInstance,
  type EntityTreeClassGroup,
  type EntitySuggestion,
  type EntitySuggestionsResponse,
  type PhaseContextForSuggestions,
} from '@/lib/api/client'
import type { EntityDomain, EntityStatus, PhaseEntity, JourneyPhaseWithMode } from '@ispot/shared'

// ============================================================================
// TYPES
// ============================================================================

export interface UseEntityTreeOptions {
  /** 'structured' shows only phase-relevant entities, 'free' shows all */
  mode: 'structured' | 'free'
  /** Filter to show only specific statuses */
  statusFilter?: EntityStatus[]
}

export interface PhaseEntityRequirement {
  classUri: string
  className: string
  domain: EntityDomain
  minCount: number
  isRequired: boolean
  currentCount: number
  confirmedCount: number
  progress: number // 0-100
}

export interface EntityTreeData {
  /** All entities (filtered by mode) */
  entities: EntityTreeInstance[]
  /** Entities grouped by domain */
  byDomain: {
    market: EntityTreeClassGroup[]
    business: EntityTreeClassGroup[]
    solution: EntityTreeClassGroup[]
  }
  /** Status counts */
  counts: {
    total: number
    pending: number
    confirmed: number
    rejected: number
  }
  /** Phase requirements with progress (structured mode only) */
  phaseRequirements: PhaseEntityRequirement[]
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export function useEntityTree(options: UseEntityTreeOptions = { mode: 'free' }) {
  const { accountId, projectId } = useSessionStore()
  const queryClient = useQueryClient()

  // Get phase configuration from journey store
  const framework = useJourneyStore((s) => s.framework)
  const journey = useJourneyStore((s) => s.journey)
  const currentPhaseId = journey?.currentPhaseId
  const getActivePhases = useJourneyStore((s) => s.getActivePhases)

  // Query for entity tree data
  const query = useQuery({
    queryKey: ['entityTree', accountId, projectId],
    queryFn: async () => {
      if (!accountId || !projectId) {
        throw new Error('Missing accountId or projectId')
      }
      return entityTreeApi.getTree(accountId, projectId)
    },
    enabled: !!accountId && !!projectId,
    staleTime: 30000, // 30 seconds
  })

  // Filter entities based on mode and phase
  const filteredData = filterEntitiesByPhase(
    query.data,
    options,
    framework,
    currentPhaseId,
    getActivePhases
  )

  return {
    // Data
    ...filteredData,

    // Query state
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,

    // Actions
    refetch: query.refetch,
    invalidate: () => {
      queryClient.invalidateQueries({ queryKey: ['entityTree', accountId, projectId] })
    },
  }
}

// ============================================================================
// VALIDATION MUTATIONS HOOK
// ============================================================================

export function useEntityValidation() {
  const { accountId, projectId } = useSessionStore()
  const queryClient = useQueryClient()

  // Update single entity status
  const updateStatus = useMutation({
    mutationFn: async ({
      entityUri,
      status,
    }: {
      entityUri: string
      status: EntityStatus
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing accountId or projectId')
      }
      return entityTreeApi.updateStatus(accountId, projectId, entityUri, status)
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues with accountId/projectId
      queryClient.invalidateQueries({ queryKey: ['entityTree'] })
      queryClient.invalidateQueries({ queryKey: ['instances'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
      // Cross-invalidate canvas queries
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })

  // Bulk validate entities
  const bulkValidate = useMutation({
    mutationFn: async ({
      entityUris,
      status,
    }: {
      entityUris: string[]
      status: 'confirmed' | 'rejected'
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing accountId or projectId')
      }
      return entityTreeApi.bulkValidate(accountId, projectId, entityUris, status)
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues with accountId/projectId
      queryClient.invalidateQueries({ queryKey: ['entityTree'] })
      queryClient.invalidateQueries({ queryKey: ['instances'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
      // Cross-invalidate canvas queries
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })

  // Helper: confirm entity
  const confirmEntity = (entityUri: string) =>
    updateStatus.mutate({ entityUri, status: 'confirmed' })

  // Helper: reject entity
  const rejectEntity = (entityUri: string) =>
    updateStatus.mutate({ entityUri, status: 'rejected' })

  // Helper: confirm all pending
  const confirmAllPending = (pendingUris: string[]) =>
    bulkValidate.mutate({ entityUris: pendingUris, status: 'confirmed' })

  // Helper: reject all pending
  const rejectAllPending = (pendingUris: string[]) =>
    bulkValidate.mutate({ entityUris: pendingUris, status: 'rejected' })

  return {
    updateStatus,
    bulkValidate,
    confirmEntity,
    rejectEntity,
    confirmAllPending,
    rejectAllPending,
    isUpdating: updateStatus.isPending || bulkValidate.isPending,
  }
}

// ============================================================================
// PENDING COUNT HOOK
// ============================================================================

export function usePendingEntityCount() {
  const { accountId, projectId } = useSessionStore()

  return useQuery({
    queryKey: ['entityPendingCount', accountId, projectId],
    queryFn: async () => {
      if (!accountId || !projectId) return 0
      const result = await entityTreeApi.getPendingCount(accountId, projectId)
      return result.count
    },
    enabled: !!accountId && !!projectId,
    staleTime: 10000, // 10 seconds
  })
}

// ============================================================================
// ENTITY CREATION HOOK
// ============================================================================

export function useEntityCreation() {
  const { accountId, projectId } = useSessionStore()
  const queryClient = useQueryClient()

  const createEntity = useMutation({
    mutationFn: async ({
      entityType,
      label,
      description,
    }: {
      entityType: string
      label: string
      description?: string
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing accountId or projectId')
      }
      return entityTreeApi.createEntity(accountId, projectId, entityType, label, description)
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues with accountId/projectId
      // Invalidate entity tree to show new entity
      queryClient.invalidateQueries({ queryKey: ['entityTree'] })
      queryClient.invalidateQueries({ queryKey: ['instances'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
      // Cross-invalidate canvas queries so Canvas view updates
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
      queryClient.invalidateQueries({ queryKey: ['ontologyInstances'] })
      queryClient.invalidateQueries({ queryKey: ['entityCounts'] })
    },
  })

  return {
    createEntity: createEntity.mutate,
    createEntityAsync: createEntity.mutateAsync,
    isCreating: createEntity.isPending,
    error: createEntity.error,
  }
}

// ============================================================================
// ENTITY SUGGESTIONS HOOK
// ============================================================================

export function useEntitySuggestions(
  entityUri: string | null,
  phaseContext?: PhaseContextForSuggestions
) {
  const { accountId, projectId } = useSessionStore()

  const query = useQuery({
    queryKey: ['entitySuggestions', accountId, projectId, entityUri, phaseContext?.phaseName],
    queryFn: async (): Promise<EntitySuggestionsResponse> => {
      if (!accountId || !projectId || !entityUri) {
        throw new Error('Missing required parameters')
      }
      return entityTreeApi.getSuggestions(accountId, projectId, entityUri, phaseContext)
    },
    enabled: !!accountId && !!projectId && !!entityUri,
    staleTime: 60000, // 1 minute - suggestions don't change often
    gcTime: 5 * 60 * 1000, // 5 minutes
  })

  return {
    suggestions: query.data?.suggestions ?? [],
    entityContext: query.data?.entityContext,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

// Re-export suggestion types for convenience
export type { EntitySuggestion, EntitySuggestionsResponse }

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function filterEntitiesByPhase(
  data: EntityTreeResponse | undefined,
  options: UseEntityTreeOptions,
  framework: ReturnType<typeof useJourneyStore.getState>['framework'],
  currentPhaseId: string | undefined,
  getActivePhases: () => JourneyPhaseWithMode[]
): EntityTreeData {
  const emptyResult: EntityTreeData = {
    entities: [],
    byDomain: { market: [], business: [], solution: [] },
    counts: { total: 0, pending: 0, confirmed: 0, rejected: 0 },
    phaseRequirements: [],
  }

  if (!data) return emptyResult

  // Free mode: return all entities
  if (options.mode === 'free') {
    return {
      ...data,
      phaseRequirements: [],
    }
  }

  // Structured mode: filter by phase requirements
  if (!framework || !currentPhaseId) {
    return {
      ...data,
      phaseRequirements: [],
    }
  }

  // Get all phases up to and including current phase
  const activePhases = getActivePhases()
  const currentPhaseIndex = activePhases.findIndex((p) => p.id === currentPhaseId)
  const relevantPhases = activePhases.slice(0, currentPhaseIndex + 1)

  // Collect all required entity classes from relevant phases
  const requiredClassUris = new Set<string>()
  const phaseEntitiesMap = new Map<string, PhaseEntity>()

  for (const phase of relevantPhases) {
    for (const entity of phase.entities || []) {
      requiredClassUris.add(entity.classUri)
      // Keep the latest phase config for each entity class
      phaseEntitiesMap.set(entity.classUri, entity)
    }
  }

  // If no phase entities are configured, show all entities (fallback to free mode behavior)
  // This provides better UX until Phase Configurator is used to configure requirements
  if (requiredClassUris.size === 0) {
    return {
      ...data,
      phaseRequirements: [],
    }
  }

  // Filter entities to only those matching required classes
  const filteredEntities = data.entities.filter((e) =>
    requiredClassUris.has(e.classUri)
  )

  // Apply status filter if provided
  const statusFilteredEntities = options.statusFilter
    ? filteredEntities.filter((e) => options.statusFilter!.includes(e.status))
    : filteredEntities

  // Rebuild byDomain with filtered entities
  const filteredByDomain = groupByDomainAndClass(statusFilteredEntities)

  // Calculate phase requirements with progress
  const phaseRequirements: PhaseEntityRequirement[] = Array.from(
    phaseEntitiesMap.entries()
  ).map(([classUri, phaseEntity]) => {
    const classEntities = statusFilteredEntities.filter((e) => e.classUri === classUri)
    const confirmedCount = classEntities.filter((e) => e.status === 'confirmed').length
    const currentCount = classEntities.length
    const minCount = phaseEntity.minCount || 1
    const progress = Math.min(100, Math.round((confirmedCount / minCount) * 100))

    return {
      classUri,
      className: phaseEntity.label || classUri.split('#').pop() || '',
      domain: getDomainFromClassUri(classUri),
      minCount,
      isRequired: phaseEntity.isRequired ?? true,
      currentCount,
      confirmedCount,
      progress,
    }
  })

  return {
    entities: statusFilteredEntities,
    byDomain: filteredByDomain,
    counts: {
      total: statusFilteredEntities.length,
      pending: statusFilteredEntities.filter((e) => e.status === 'pending').length,
      confirmed: statusFilteredEntities.filter((e) => e.status === 'confirmed').length,
      rejected: statusFilteredEntities.filter((e) => e.status === 'rejected').length,
    },
    phaseRequirements,
  }
}

function groupByDomainAndClass(entities: EntityTreeInstance[]): {
  market: EntityTreeClassGroup[]
  business: EntityTreeClassGroup[]
  solution: EntityTreeClassGroup[]
} {
  const groups: Record<EntityDomain, Map<string, EntityTreeClassGroup>> = {
    market: new Map(),
    business: new Map(),
    solution: new Map(),
  }

  for (const entity of entities) {
    const domainMap = groups[entity.domain]
    if (!domainMap) continue

    let classGroup = domainMap.get(entity.classUri)
    if (!classGroup) {
      classGroup = {
        classUri: entity.classUri,
        className: entity.className,
        domain: entity.domain,
        instances: [],
      }
      domainMap.set(entity.classUri, classGroup)
    }

    classGroup.instances.push(entity)
  }

  return {
    market: Array.from(groups.market.values()),
    business: Array.from(groups.business.values()),
    solution: Array.from(groups.solution.values()),
  }
}

function getDomainFromClassUri(classUri: string): EntityDomain {
  if (classUri.includes('/market#')) return 'market'
  if (classUri.includes('/business#')) return 'business'
  if (classUri.includes('/solution#')) return 'solution'
  return 'market'
}

export default useEntityTree
